import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Edit3, Trash2, Save, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 focus:border-spike-blue focus:ring-2 focus:ring-spike-blue/20 outline-none";
const labelCls = "block text-xs font-medium text-slate-700 mb-1.5";

const SpecialDateForm = ({ initial, venueId, timeSlots, onCancel, onSaved }) => {
  const [date, setDate] = useState(initial?.date || "");
  const [label, setLabel] = useState(initial?.label || "");
  const [overrides, setOverrides] = useState(() => {
    const ov = initial?.slot_price_overrides || {};
    return timeSlots.map((ts) => ({
      time: ts.time,
      default_price: ts.price_cad,
      override_price: ov[ts.time] ?? "",
    }));
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const save = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      toast.error("Date must be YYYY-MM-DD");
      return;
    }
    const slot_price_overrides = {};
    overrides.forEach((o) => {
      if (o.override_price !== "" && !isNaN(Number(o.override_price))) {
        slot_price_overrides[o.time] = Number(o.override_price);
      }
    });
    setBusy(true);
    try {
      const body = { date, label, slot_price_overrides };
      if (isEdit) {
        await api.patch(`/admin/special-dates/${initial.id}`, body, { withCredentials: true });
        toast.success("Special date updated.");
      } else {
        await api.post(`/admin/venues/${venueId}/special-dates`, body, { withCredentials: true });
        toast.success("Special date created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-spike-blue/40 rounded-lg p-5 shadow-card-hover" data-testid="sd-form">
      <div className="font-semibold text-slate-900 mb-4">{isEdit ? `Edit · ${initial.date}` : "New special date"}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div>
          <label className={labelCls}>Date (YYYY-MM-DD)</label>
          <input type="date" data-testid="sd-input-date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Label</label>
          <input data-testid="sd-input-label" className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Christmas Day" />
        </div>
      </div>
      <div>
        <label className={labelCls}>Price overrides — leave blank to use default</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {overrides.map((o, idx) => (
            <div key={o.time} className="border border-slate-200 rounded p-2 bg-slate-50">
              <div className="text-xs font-medium text-slate-600">{o.time}</div>
              <div className="text-xs text-slate-400">default ${o.default_price}</div>
              <input
                type="number"
                min={0}
                step="0.01"
                data-testid={`sd-override-${o.time.replace(":", "")}`}
                className="mt-1 w-full bg-white border border-slate-300 rounded px-2 py-1 text-sm"
                placeholder="—"
                value={o.override_price}
                onChange={(e) => {
                  const next = [...overrides];
                  next[idx] = { ...next[idx], override_price: e.target.value };
                  setOverrides(next);
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2 mt-5">
        <button type="button" data-testid="sd-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-md">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
        <button type="button" data-testid="sd-cancel" onClick={onCancel} className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium px-4 py-2 rounded-md">Cancel</button>
      </div>
    </div>
  );
};

const AdminSpecialDates = () => {
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState("");
  const [items, setItems] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/venues", { withCredentials: true });
        setVenues(data);
        if (data.length) setVenueId(data[0].id);
      } catch {
        toast.error("Could not load venues.");
      }
    })();
  }, []);

  const reload = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const [sd, ts] = await Promise.all([
        api.get(`/admin/venues/${venueId}/special-dates`, { withCredentials: true }),
        api.get(`/admin/venues/${venueId}/time-slots`, { withCredentials: true }),
      ]);
      setItems(sd.data);
      setTimeSlots(ts.data);
    } catch {
      toast.error("Could not load.");
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!window.confirm("Delete this special date?")) return;
    try {
      await api.delete(`/admin/special-dates/${id}`, { withCredentials: true });
      toast.success("Deleted.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-special-dates">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-spike-blue" /> Special Dates
          </h2>
          <p className="text-sm text-slate-600 mt-1">Holiday / event-specific price overrides per time slot.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select data-testid="sd-venue-select" value={venueId} onChange={(e) => setVenueId(e.target.value)} className={inputCls + " w-auto"}>
            {venues.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
          </select>
          {!creating && !editing && (
            <button type="button" data-testid="sd-add-new" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-white text-sm font-medium px-4 py-2 rounded-md">
              <Plus className="w-4 h-4" /> Add date
            </button>
          )}
        </div>
      </div>

      {creating && (
        <SpecialDateForm initial={null} venueId={venueId} timeSlots={timeSlots} onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); reload(); }} />
      )}

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-lg p-5 shadow-card" data-testid={`sd-card-${s.id}`}>
              {editing === s.id ? (
                <SpecialDateForm initial={s} venueId={venueId} timeSlots={timeSlots} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
              ) : (
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div className="font-semibold text-slate-900">{s.date}{s.label && <span className="ml-2 text-slate-600 font-normal">— {s.label}</span>}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Object.entries(s.slot_price_overrides || {}).map(([t, p]) => (
                        <span key={t} className="text-xs bg-spike-blue-pale text-spike-blue px-2 py-1 rounded">{t} → ${p}</span>
                      ))}
                      {Object.keys(s.slot_price_overrides || {}).length === 0 && (
                        <span className="text-xs text-slate-400">no overrides set</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button type="button" data-testid={`sd-edit-${s.id}`} onClick={() => setEditing(s.id)} className="p-2 text-slate-600 hover:text-spike-blue rounded">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button type="button" data-testid={`sd-delete-${s.id}`} onClick={() => remove(s.id)} className="p-2 text-slate-600 hover:text-red-500 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500" data-testid="sd-empty">
              No special dates yet. Add holiday or event pricing.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSpecialDates;
